import { SaveCancelWidget } from 'components';
import { FlowActionTypes, FlowContext } from 'contexts/FlowContext';
import { CACHE_FIRST, EDITOR_PATH_ID, ENTITY_PATH_ID } from 'contracts/constants';
import type { ActionButtonProps } from 'contracts/interfaces/ActionButtonProps';
import { isEqual } from 'lodash';
import type { FC } from 'react';
import { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  GetFlowDocument,
  useGetEndpointsByPackageVersionQuery,
  useGetFlowQuery,
  useGetFlowsByPackageVersionQuery,
  useGetMappingsByPackageVersionQuery,
  useGetModelsByPackageVersionQuery,
  useUpdateFlowSchemaMutation
} from 'services/graphql/generated/hooks';
import { getParams } from 'utils';

export const FlowActionButtons: FC<ActionButtonProps> = ({ isEditable }) => {
  const { pathname } = useLocation();
  // const editorParams: any = getParams(pathname, [EDITOR_PATH_ID]);
  // if not loaded form editor path, Try to load it from entity path
  const params = getParams(pathname, [EDITOR_PATH_ID, ENTITY_PATH_ID]); // editorParams.entityId ? editorParams : getParams(pathname, [ENTITY_PATH_ID]);
  const ctx = useContext(FlowContext);
  const [isSaved, setIsSaved] = useState(false);

  useEffect(() => {
    setIsSaved(false);
  }, [ctx?.state.flowSchema]);

  const { data } = useGetFlowQuery({
    ...CACHE_FIRST,
    variables: {
      packageVersionUuid: params.versionUuid,
      uuid: params.entityId
    },
    skip: !(params.versionUuid && params.entityId)
  });

  const variables = {
    offset: 0,
    limit: 20,
    packageVersionUuid: params.versionUuid
  };

  const { data: mappingsData, loading: loadingMappings } = useGetMappingsByPackageVersionQuery({
    variables,
    ...CACHE_FIRST,
    skip: !params.versionUuid
  });

  const { data: endpointsData, loading: loadingEndpoints } = useGetEndpointsByPackageVersionQuery({
    variables,
    ...CACHE_FIRST,
    skip: !params.versionUuid
  });

  const { data: modelsData, loading: loadingModels } = useGetModelsByPackageVersionQuery({
    variables,
    ...CACHE_FIRST,
    skip: !params.versionUuid
  });

  const { data: flowsData, loading: loadingFlows } = useGetFlowsByPackageVersionQuery({
    variables,
    ...CACHE_FIRST,
    skip: !params.versionUuid
  });

  /* TODO: Handle the error from this operation. Ticket for the implementation:
     https://deitycommerce.atlassian.net/browse/DI-564 */
  const [updateFlowSchema, { loading: isSaving, error: saveError }] = useUpdateFlowSchemaMutation({
    refetchQueries: [
      {
        query: GetFlowDocument,
        variables: {
          packageVersionUuid: params.versionUuid,
          uuid: params.entityId
        }
      }
    ]
  });

  const saveFlow = () => {
    if (params?.entityId && ctx?.state) {
      updateFlowSchema({
        variables: {
          packageVersionUuid: params.versionUuid,
          uuid: params.entityId,
          schema: ctx.state.flowSchema
        },
        notifyOnNetworkStatusChange: true
      }).then(() => {
        setIsSaved(true);
      });
    }
  };

  const cancelChange = () => {
    // window.location.reload();
    if (data && endpointsData && mappingsData && modelsData && flowsData) {
      ctx?.dispatch({
        type: FlowActionTypes.INIT_FLOW,
        payload: {
          uuid: data.getFlow.uuid,
          schema: data.getFlow.schema,
          endpoints: endpointsData.getEndpointsByPackageVersion.data,
          mappings: mappingsData.getMappingsByPackageVersion.data,
          models: modelsData.getModelsByPackageVersion.data,
          flows: flowsData?.getFlowsByPackageVersion.data,
          locked: ctx.state.locked
        }
      });
    }
  };

  const renderCondition =
    isEditable &&
    !isSaved &&
    data?.getFlow.schema &&
    ctx?.state.flowSchema &&
    data?.getFlow.schema &&
    !isEqual(data?.getFlow.schema, ctx?.state.flowSchema);

  return isSaving || renderCondition ? (
    <SaveCancelWidget onCancel={cancelChange} onSave={saveFlow} isSaving={isSaving} />
  ) : null;
};
